// vg-binding: P7.D-08
// vg-binding: P7.D-18
// vg-binding: P7.D-159
// vg-binding: P7.D-160
//
// apps/vendor/src/features/products/api.ts
//
// TanStack-Query hooks wrapping the Wave 7 BE product endpoints.
// Amendment #3 (..160, Tasks 82): add useProductDetail +
// useUpdateProduct for the new /products/:id and /products/:id/edit pages.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

export type ProductState =
  | 'pending'
  | 'live'
  | 'inactive_countdown'
  | 'inactive'
  | 'rejected';

export type SubmissionMethod = 'catalog' | 'file' | 'api';

export interface ProductRow {
  _id: string;
  name: string;
  sku_prefix: string;
  product_type: string;
  state: ProductState;
  cost_price_cents: number;
  // Bug 7 — dimensions are per-variant now (ProductDetailVariant.dimensions).
  // Amendment #4 — processing_time_days / capacity_per_day
  // weight_grams are per-variant now (ProductDetailVariant).
  low_stock_threshold: number;
  reject_reason: string | null;
  inactive_countdown_starts_at: number | null;
  inactive_countdown_ends_at: number | null;
  submission_method: SubmissionMethod;
  /** 2026-10-13 — Printway blueprint this product maps to (file import). */
  source_id?: string | null;
  version: number;
  /** Cover image URL derived from the first variant's first image. */
  cover_image_url: string | null;
  /** D17a — the vendor's own cost range across this product's SKUs (never a tier). */
  cost_min_cents?: number | null;
  cost_max_cents?: number | null;
}

interface ApiOk<T> { ok: true; data: T }
interface ApiErr {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    current_state?: string;
    current_version?: number;
    field_errors?: Record<string, string>;
  };
}

export class ProductsApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly userMessage: string | undefined;
  public readonly fieldErrors: Record<string, string> | undefined;
  public readonly currentVersion: number | undefined;
  constructor(
    code: string,
    message: string,
    statusCode: number,
    extras?: {
      user_message?: string;
      field_errors?: Record<string, string>;
      current_version?: number;
    },
  ) {
    super(message);
    this.name = 'ProductsApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.userMessage = extras?.user_message;
    this.fieldErrors = extras?.field_errors;
    this.currentVersion = extras?.current_version;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as ApiOk<T> | ApiErr | null;
  if (!body) throw new ProductsApiError('NETWORK', 'Empty body', res.status);
  if (!body.ok) {
    const e = body.error;
    throw new ProductsApiError(e.code, e.message, res.status, {
      ...(e.user_message !== undefined ? { user_message: e.user_message } : {}),
      ...(e.field_errors !== undefined ? { field_errors: e.field_errors } : {}),
      ...(e.current_version !== undefined
        ? { current_version: e.current_version }
        : {}),
    });
  }
  return body.data;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface ProductsListData {
  rows: ProductRow[];
  meta: {
    next_cursor: string | null;
    total_returned: number;
    total: number;
    page: number;
    page_count: number;
    limit: number;
  };
}

export interface ProductsListParams {
  state?: ProductState | 'all';
  /** DEPRECATED — prefer `page`. */
  cursor?: string;
  /** 1-based page (BR-PAGING numbered nav). */
  page?: number;
  limit?: number;
  /** Free-text search on product name (regex, case-insensitive). */
  q?: string;
}

export function useProductsList(
  params: ProductsListParams,
): UseQueryResult<ProductsListData, ProductsApiError> {
  return useQuery<ProductsListData, ProductsApiError>({
    queryKey: ['vendor', 'products', 'list', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.state && params.state !== 'all') qs.set('state', params.state);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.q && params.q.trim().length > 0) qs.set('q', params.q.trim());
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/products${qstr ? `?${qstr}` : ''}`,
        { credentials: 'include' },
      );
      return unwrap<ProductsListData>(res);
    },
  });
}

// ---------------------------------------------------------------------------
// Catalog (blueprint picker source for /products/new)
//
// dbg-vendor-catalog-picker — wires the Add Product wizard's CatalogPicker
// to the live BE list endpoint. The Wave 8 shell shipped a free-form text
// input because the read endpoint was not yet live; GET
// /api/v1/vendor/catalog/blueprints (merchant.catalog.routes.ts) is now
// available and returns active blueprints with pricing stripped.
// ---------------------------------------------------------------------------

export interface CatalogBlueprintVariant {
  _id?: string;
  sku?: string;
}

/**
 * Blueprint name as served by the catalog endpoint. The BE
 * `pickMerchantPublicFields` mapper passes the raw Mongo `name` through,
 * which is a localized `{ en, vi }` object — NOT a plain string. Some
 * older docs / mappers emit a plain string, so accept both.
 */
export type LocalizedName = string | { en?: string; vi?: string; fr?: string };

/**
 * Shape returned by GET /api/v1/vendor/catalog/blueprints, mapped through
 * `pickMerchantPublicFields`: `_id` is renamed to `id`, pricing is
 * stripped, and a `coverImageUrl` + `variantCount` are derived. Fields
 * past `id`/`name`/`slug` are optional because the deployed mapper has
 * surfaced different subsets over time — render defensively.
 */
export interface CatalogBlueprint {
  id: string;
  name: LocalizedName;
  slug?: string;
  blueprintCode?: string | null;
  coverImageUrl?: string | null;
  variantCount?: number;
  variants?: CatalogBlueprintVariant[];
}

export interface CatalogListData {
  items: CatalogBlueprint[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CatalogListParams {
  /** Free-text search on blueprint name (BE: $text index). */
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useVendorCatalog(
  params: CatalogListParams,
): UseQueryResult<CatalogListData, ProductsApiError> {
  return useQuery<CatalogListData, ProductsApiError>({
    queryKey: ['vendor', 'catalog', 'blueprints', params],
    staleTime: 60_000, // catalog changes rarely; 60s cache is plenty
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.search && params.search.trim().length > 0) {
        qs.set('search', params.search.trim());
      }
      if (params.page) qs.set('page', String(params.page));
      if (params.pageSize) qs.set('pageSize', String(params.pageSize));
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/catalog/blueprints${qstr ? `?${qstr}` : ''}`,
        { credentials: 'include' },
      );
      // The catalog endpoint (merchant.catalog.routes.ts) wraps its reply
      // with the BE `ok()` helper → `{ success: true, data }`, NOT the
      // `{ ok: true, data }` shape the product endpoints use. The shared
      // `unwrap()` keys on `body.ok`, so it would throw on every catalog
      // response. Parse the `success` envelope directly here.
      const body = (await res.json().catch(() => null)) as
        | { success?: boolean; ok?: boolean; data?: CatalogListData; error?: { code?: string; message?: string } }
        | null;
      if (!body) {
        throw new ProductsApiError('NETWORK', 'Empty body', res.status);
      }
      const okFlag = body.success ?? body.ok ?? false;
      if (!okFlag || !body.data) {
        throw new ProductsApiError(
          body.error?.code ?? 'CATALOG_ERROR',
          body.error?.message ?? 'Catalog request failed',
          res.status,
        );
      }
      return body.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

export interface ProductSubmitPayload {
  method: SubmissionMethod;
  source_id?: string;
  name: string;
  sku_prefix: string;
  product_type: string;
  cost_price_cents: number;
  // Bug 7 — product-level dimensions removed; each variant carries its own.
  // Amendment #4 — product-level processing/capacity/weight removed;
  // each variant carries its own.
  warehouses_assigned: string[];
  low_stock_threshold: number;
  variants: Array<{
    size: string;
    color: string;
    enabled: boolean;
    cost_markup_cents: number;
    /** D13 — absolute per-SKU cost; absent on the manual form (base + markup). */
    cost_cents?: number | null;
    image_urls: string[];
    weight_grams_override?: number;
    dimensions: { length: number; width: number; height: number };
    // Amendment #4 — per-SKU operational fields (required on submit).
    processing_time_days: number;
    capacity_per_day: number;
    weight_grams: number;
    // Amendment #4 — external-system SKU mapping (optional).
    external_sku?: string;
    // Amendment #4 — chosen blueprint option-value refs (catalog path).
    option_value_ids?: string[];
  }>;
}

export interface ProductSubmitResult {
  product_id: string;
  state: 'pending';
  variants_count: number;
  submitted_at: number;
}

const PRODUCT_LIST_KEY = ['vendor', 'products', 'list'] as const;
const PRODUCT_HISTORY_KEY = ['vendor', 'products', 'history'] as const;

export function useSubmitProduct(): UseMutationResult<
  ProductSubmitResult,
  ProductsApiError,
  ProductSubmitPayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await csrfFetch('/api/v1/vendor/products', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return unwrap<ProductSubmitResult>(res);
    },
    onSuccess: async () => {
      // dbg-r2 Bug B — await + refetchType:'all' so an inactive list query
      // (user is on /products/new at mutate time) is refetched as soon as
      // it remounts on /products. Without 'all', only active observers
      // refetch; with the default 30s staleTime in main.tsx, a freshly
      // mounted list reuses stale cache for half a minute.
      await qc.invalidateQueries({
        queryKey: PRODUCT_LIST_KEY,
        refetchType: 'all',
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Lifecycle mutations (PATCH / inactivate / cancel / reactivate / resubmit)
// ---------------------------------------------------------------------------

async function patchProductCmd<TBody, TResult>(opts: {
  product_id: string;
  version: number;
  path: string; // e.g. "" → /products/:id ; "/inactivate" → /products/:id/inactivate
  method: 'PATCH' | 'POST';
  body?: TBody;
}): Promise<TResult> {
  const url = `/api/v1/vendor/products/${opts.product_id}${opts.path}`;
  // dbg-r2 Bug A — Fastify rejects "Body cannot be empty when content-type
  // is set to 'application/json'". Only declare Content-Type when we
  // actually send a body. Cancel-inactivate, reactivate, resubmit have no
  // payload, so they used to hit this error every click.
  const headers: Record<string, string> = {
    'If-Match': String(opts.version),
  };
  const init: RequestInit = {
    method: opts.method,
    credentials: 'include',
    headers,
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  const res = await csrfFetch(url, init);
  return unwrap<TResult>(res);
}

export function useInactivateProduct(): UseMutationResult<
  { state: 'inactive_countdown'; ends_at: number; to_version: number },
  ProductsApiError,
  { product_id: string; version: number; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      patchProductCmd<{ reason?: string }, { state: 'inactive_countdown'; ends_at: number; to_version: number }>({
        product_id: input.product_id,
        version: input.version,
        path: '/inactivate',
        method: 'POST',
        body: input.reason ? { reason: input.reason } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRODUCT_LIST_KEY });
    },
  });
}

export function useCancelInactivate(): UseMutationResult<
  { state: 'live'; to_version: number },
  ProductsApiError,
  { product_id: string; version: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      patchProductCmd<undefined, { state: 'live'; to_version: number }>({
        product_id: input.product_id,
        version: input.version,
        path: '/cancel-inactivate',
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRODUCT_LIST_KEY });
    },
  });
}

export function useReactivateProduct(): UseMutationResult<
  { state: 'live'; to_version: number },
  ProductsApiError,
  { product_id: string; version: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      patchProductCmd<undefined, { state: 'live'; to_version: number }>({
        product_id: input.product_id,
        version: input.version,
        path: '/reactivate',
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRODUCT_LIST_KEY });
    },
  });
}

export function useResubmitProduct(): UseMutationResult<
  { state: 'pending'; to_version: number },
  ProductsApiError,
  { product_id: string; version: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      patchProductCmd<undefined, { state: 'pending'; to_version: number }>({
        product_id: input.product_id,
        version: input.version,
        path: '/resubmit',
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRODUCT_LIST_KEY });
    },
  });
}

export function useApprovalHistory(
  product_id: string | undefined,
): UseQueryResult<
  {
    rows: Array<{
      event: string;
      actor: string;
      reason: string | null;
      occurred_at: number;
      diff: unknown;
    }>;
  },
  ProductsApiError
> {
  return useQuery({
    queryKey: [...PRODUCT_HISTORY_KEY, product_id],
    enabled: Boolean(product_id),
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/products/${product_id}/approval-history`,
        { credentials: 'include' },
      );
      return unwrap<{
        rows: Array<{
          event: string;
          actor: string;
          reason: string | null;
          occurred_at: number;
          diff: unknown;
        }>;
      }>(res);
    },
  });
}

// ---------------------------------------------------------------------------
// Single product detail (Amendment #3 Task 82)
// ---------------------------------------------------------------------------

export interface ProductDetailVariant {
  _id: string;
  product_id?: string;
  size: string;
  color: string;
  enabled: boolean;
  cost_markup_cents: number;
  /** 2026-10-14 (D13) — the SKU's own cost (file import); null = base + markup. */
  cost_cents?: number | null;
  image_urls: string[];
  weight_grams_override?: number;
  /** Bug 7 — per-variant packaging dimensions (cm). */
  dimensions?: { length: number; width: number; height: number };
  /** Amendment #4 — per-SKU operational fields. */
  processing_time_days?: number;
  capacity_per_day?: number;
  weight_grams?: number;
  /** Amendment #4 — external-system SKU mapping. */
  external_sku?: string | null;
  /** Printway SKU (mapped products) or the prefix-derived SKU. */
  sku?: string;
  /** 2026-10-13 — Printway variant this row fulfils; null = unmapped. */
  printway_variant_id?: string | null;
}

export interface ProductDetailData {
  product: ProductRow;
  variants: ProductDetailVariant[];
  approval_history_summary: {
    last_event: string | null;
    last_event_at: number | null;
    total_events: number;
  };
}

const PRODUCT_DETAIL_KEY = ['vendor', 'products', 'detail'] as const;

export function useProductDetail(
  product_id: string | undefined,
): UseQueryResult<ProductDetailData, ProductsApiError> {
  return useQuery<ProductDetailData, ProductsApiError>({
    queryKey: [...PRODUCT_DETAIL_KEY, product_id],
    enabled: Boolean(product_id),
    staleTime: 30_000, // 30s — detail view is not real-time critical
    queryFn: async () => {
      const res = await csrfFetch(`/api/v1/vendor/products/${product_id}`, {
        credentials: 'include',
      });
      return unwrap<ProductDetailData>(res);
    },
  });
}

// ---------------------------------------------------------------------------
// Update product (Amendment #3 Task 82)
//
// Maps to PATCH /api/v1/vendor/products/:id. The BE (see
// productEditService.editProduct + dual-write contract) handles
// the Live→Pending re-approval flip and ProductApproval row creation
// when one of the 5 trigger fields changes on a live product.
// The FE just submits the full edit payload; no client-side branching on
// trigger fields needed beyond UX warning highlights.
// ---------------------------------------------------------------------------

export interface UpdateProductInput {
  product_id: string;
  version: number;
  name?: string;
  sku_prefix?: string;
  product_type?: string;
  cost_price_cents?: number;
  // Amendment #4 — product-level processing/capacity/weight removed;
  // edit per-variant via variants[] below.
  low_stock_threshold?: number;
  // dbg-r3 — variants are upserted server-side on (size, color). Missing
  // variants are NOT deleted, so partial payloads only add/update; removal
  // is a separate flow yet to be wired.
  variants?: Array<{
    size: string;
    color: string;
    enabled?: boolean;
    cost_markup_cents?: number;
    /** D16 — per-SKU cost; required for a new variant of a per-SKU product. */
    cost_cents?: number | null;
    image_urls?: string[];
    weight_grams_override?: number;
    // Bug 7 — per-variant dimensions (partial on edit).
    dimensions?: { length?: number; width?: number; height?: number };
    // Amendment #4 — per-SKU operational fields (optional on edit).
    processing_time_days?: number;
    capacity_per_day?: number;
    weight_grams?: number;
    // Amendment #4 — external-system SKU mapping (optional).
    external_sku?: string;
    printway_variant_id?: string;
  }>;
}

export interface UpdateProductResult {
  state: ProductState;
  to_version: number;
  /** True when the edit triggered re-approval (Live → Pending flip). */
  reapproval_triggered?: boolean;
}

export function useUpdateProduct(): UseMutationResult<
  UpdateProductResult,
  ProductsApiError,
  UpdateProductInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const { product_id, version, ...body } = input;
      return patchProductCmd<typeof body, UpdateProductResult>({
        product_id,
        version,
        path: '',
        method: 'PATCH',
        body,
      });
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: PRODUCT_LIST_KEY });
      void qc.invalidateQueries({
        queryKey: [...PRODUCT_DETAIL_KEY, input.product_id],
      });
      void qc.invalidateQueries({
        queryKey: [...PRODUCT_HISTORY_KEY, input.product_id],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Catalog blueprint DETAIL (Amendment #4 Task 09)
//
// GET /api/v1/vendor/catalog/blueprints/:id returns the blueprint's
// variants[] (sku, optionValueIds, dimensions cm→{l,w,h}, weightGrams —
// pricing stripped) PLUS resolved option axes for the VariantOptionSelector.
// Like useVendorCatalog, this endpoint wraps with the BE `ok()` helper
// (`{ success: true, data }`), NOT the product `{ ok: true, data }` shape.
// ---------------------------------------------------------------------------

export interface CatalogOptionValue {
  optionValueId: string;
  code: string;
  name: LocalizedName;
}

export interface CatalogOptionAxis {
  optionId: string;
  code: string;
  name: LocalizedName;
  values: CatalogOptionValue[];
}

export interface CatalogBlueprintDetailVariant {
  _id: string;
  sku: string;
  optionValueIds: string[];
  dimensions: { length: number; width: number; height: number };
  weightGrams: number;
}

export interface CatalogBlueprintDetail {
  id: string;
  name: LocalizedName;
  slug?: string | null;
  blueprintCode?: string | null;
  skuTemplate?: string | null;
  optionAxes: CatalogOptionAxis[];
  variants: CatalogBlueprintDetailVariant[];
}

export function useVendorCatalogBlueprintDetail(
  id: string | undefined,
): UseQueryResult<CatalogBlueprintDetail, ProductsApiError> {
  return useQuery<CatalogBlueprintDetail, ProductsApiError>({
    queryKey: ['vendor', 'catalog', 'blueprint', id],
    enabled: Boolean(id),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await csrfFetch(`/api/v1/vendor/catalog/blueprints/${id}`, {
        credentials: 'include',
      });
      const body = (await res.json().catch(() => null)) as
        | {
            success?: boolean;
            ok?: boolean;
            data?: CatalogBlueprintDetail;
            error?: { code?: string; message?: string };
          }
        | null;
      if (!body) {
        throw new ProductsApiError('NETWORK', 'Empty body', res.status);
      }
      const okFlag = body.success ?? body.ok ?? false;
      if (!okFlag || !body.data) {
        throw new ProductsApiError(
          body.error?.code ?? 'CATALOG_ERROR',
          body.error?.message ?? 'Catalog detail request failed',
          res.status,
        );
      }
      return body.data;
    },
  });
}

// ---------------------------------------------------------------------------
// File import with Printway SKU mapping (2026-10-13)
// ---------------------------------------------------------------------------

export interface ImportRowError {
  row: number;
  field: string;
  code: string;
}

export interface ImportRowWarning {
  row: number;
  field: 'size' | 'color';
  code: 'LABEL_MISMATCH';
  printway_value: string;
}

export interface ImportReport {
  dry_run: boolean;
  products: Array<{
    product_code: string;
    product_name: string;
    blueprint_id: string;
    blueprint_name: string;
    variant_count: number;
  }>;
  errors: ImportRowError[];
  warnings: ImportRowWarning[];
  imported_products: number;
  imported_variants: number;
}

/** A commit refused for row errors (422) still carries the full report. */
export class ImportRejectedError extends ProductsApiError {
  constructor(public readonly report: ImportReport, status: number) {
    super('IMPORT_HAS_ERRORS', 'Import has row errors', status);
  }
}

export async function postProductImport(
  rows: Array<Record<string, unknown>>,
  dryRun: boolean,
): Promise<ImportReport> {
  const res = await csrfFetch('/api/v1/vendor/products/import-excel', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, dry_run: dryRun }),
  });
  if (res.status === 422) {
    const body = (await res.json().catch(() => null)) as
      | { ok: false; error: { details?: ImportReport } }
      | null;
    if (body?.error.details) throw new ImportRejectedError(body.error.details, 422);
  }
  return unwrap<ImportReport>(res);
}

export interface PrintwaySku {
  blueprint_id: string;
  blueprint_name: string;
  sku: string;
  variant_id: string;
  size: string;
  color: string;
}

export async function fetchPrintwaySkus(opts: { blueprintId?: string; unlisted?: boolean } = {}): Promise<PrintwaySku[]> {
  const q = new URLSearchParams();
  if (opts.blueprintId) q.set('blueprint_id', opts.blueprintId);
  if (opts.unlisted) q.set('unlisted', '1');
  const res = await csrfFetch(`/api/v1/vendor/products/printway-skus${q.size ? `?${q}` : ''}`, {
    credentials: 'include',
  });
  return (await unwrap<{ skus: PrintwaySku[] }>(res)).skus;
}

export function usePrintwaySkus(opts: { blueprintId?: string; unlisted?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['vendor', 'products', 'printway-skus', opts.blueprintId ?? null, !!opts.unlisted],
    queryFn: () => fetchPrintwaySkus(opts),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });
}

export { PRODUCT_LIST_KEY as VENDOR_PRODUCT_LIST_KEY };
